import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { v4 } from 'uuid';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { GrowlSlice } from '@/store/growl/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api/index.ts';
import type { AppListenerEffectAPI } from '@/store/orders/listeners/registerEspOrderListeners.ts';

export const registerAlgoCommandListener = (startAppListening: StartAppListening) => {
  startAppListening({
    predicate: algoOrderApi.endpoints.resumeAlgo.matchRejected,
    // engage ESP stream for the newly created order tile
    effect: async (action, api) => {
      const {
        meta: {
          arg: {
            originalArgs: { orderRef: tradeId },
          },
        },
        payload: error,
      } = action;

      algoCommandError(api, tradeId, 'resume', error);
    },
  });
  startAppListening({
    predicate: algoOrderApi.endpoints.pauseAlgo.matchRejected,
    // engage ESP stream for the newly created order tile
    effect: async (action, api) => {
      const {
        meta: {
          arg: {
            originalArgs: { orderRef: tradeId },
          },
        },
        payload: error,
      } = action;

      algoCommandError(api, tradeId, 'pause', error);
    },
  });
  startAppListening({
    predicate: algoOrderApi.endpoints.fillNowAlgo.matchRejected,
    // engage ESP stream for the newly created order tile
    effect: async (action, api) => {
      const {
        meta: {
          arg: {
            originalArgs: { orderRef: tradeId },
          },
        },
        payload: error,
      } = action;

      algoCommandError(api, tradeId, 'fill', error);
    },
  });
};

const algoCommandError = (
  api: AppListenerEffectAPI,
  tradeId: string,
  commandType: 'pause' | 'fill' | 'resume',
  error: FetchBaseQueryError | undefined,
) => {
  const dispatch = api.dispatch;

  logger.logError('An error occured while cancelling order: {error_s}', error);

  dispatch(
    GrowlSlice.actions.create({
      growlId: v4(),
      growl: {
        type: 'algoCommandErrorFeedback',
        data: {
          action: commandType,
          tradeId,
        },
        createdAt: Date.now(),
        options: { autoCloseAfter: 3000 },
      },
    }),
  );

  const isTimeOut = error?.status === 'TIMEOUT_ERROR' || error?.status === 504;
  if (!isTimeOut) {
    return;
  }

  logger.logError('Order cancellation got timeout: {tradeId_s}', tradeId);
  const params: SendHelpRequestRequest = {
    config: 'orderpending',
    details: JSON.stringify({
      payload: error,
      orderIdToUpdate: tradeId, //tileId,
    }),
    data: {
      orderDescription: commandType,
      orderType: 'limit',
      /**
       * send 'g10' or 'emerging' on order creation when we are able to differentiate
       */
      orderCurrencyType: 'unknown',
      orderId: tradeId, // tileId,
    },
    templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
    templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
  };

  logger.logError('Order submission failed on timeout for {tradeId_s}', tradeId);
  dispatch(sendHelpRequestThunk(params));
};
